import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Box, Button, HStack, Icon, Spinner, Text, VStack } from "native-base";
import React, { useEffect, useRef, useState } from "react";
import { TextInput } from "react-native";
import styled from "styled-components/native";

import SearchInput from "@/components/atoms/SearchInput";
import { useDebounce } from "@/hooks/application/debounce";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useGlobalSearch, useInStoreSearch } from "@/hooks/domain/search";
import { Product } from "@/interfaces/domain/Product";
import { RootNavigation } from "@/navigation/Root";
import { HomeNavigation } from "@/navigation/stacks/HomeStack";
import EmptyCard from "@/screens/Search/components/EmptyCard";
import ProductCard from "@/screens/Search/components/ProductCard";

type WebSearchDropdownProps = {
  storeId?: string;
  disableBuiltInSearch?: boolean;
  onChangeSearch?: (text: string) => void;
};

const WebSearchDropdown = ({
  storeId = undefined,
  disableBuiltInSearch = false,
  onChangeSearch,
}: WebSearchDropdownProps) => {
  const rootNavigation = useAppNavigation<RootNavigation>();
  const homeNavigation = useAppNavigation<HomeNavigation>();

  const searchInputRef = useRef<TextInput>();

  const [products, setProducts] = useState<Product[]>();
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [dropOpen, setDropOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 1000);

  useEffect(() => {
    rootNavigation.getState()?.routes?.forEach((r) => {
      const routeQuery = (r.params as any)?.query;
      if (routeQuery !== undefined) {
        setQuery(routeQuery);
      }
    });
  }, []);

  const { isLoading: loadingGlobal, isFetching: fetchingGlobal } = useGlobalSearch({
    query: debouncedQuery,
    limit: 10,
    options: {
      enabled: debouncedQuery?.length >= 3 && !storeId && !disableBuiltInSearch,
      onSuccess: (data) => {
        setProducts(data?.produtos);
        setTotal(data?.total);
        if (searchInputRef?.current?.isFocused() && !disableBuiltInSearch) {
          setDropOpen(true);
        }
      },
    },
  });

  const { isLoading: loadingInStore, isFetching: fetchingInStore } = useInStoreSearch({
    query: debouncedQuery,
    storeId,
    limit: 10,
    options: {
      enabled: debouncedQuery?.length >= 3 && !!storeId && !disableBuiltInSearch,
      onSuccess: (data) => {
        setProducts(data?.pages.map((page) => page.produtos).flat());
        setTotal(data?.pages[0]?.total);
        if (searchInputRef?.current?.isFocused() && !disableBuiltInSearch) {
          setDropOpen(true);
        }
      },
    },
  });

  const isLoading = (loadingGlobal && fetchingGlobal) || (loadingInStore && fetchingInStore);

  const handleSearch = (text: string) => {
    setQuery(text);
    if (disableBuiltInSearch) {
      onChangeSearch(text);
    }
  };

  const clearText = () => {
    setQuery("");
    setProducts(null);
    setTotal(0);
  };

  const onPressViewMore = () => {
    if (storeId) {
      homeNavigation.navigate("inStoreSearch", {
        storeId,
        query,
      });
    } else {
      rootNavigation.navigateBetweenStacks("search", null, {
        query,
      });
    }
  };

  const onSelectProduct = (productId: string) => {
    if (storeId) {
      homeNavigation.navigate("product", {
        productId,
      });
    } else {
      rootNavigation.navigateBetweenStacks("homeStack", "product", {
        productId,
      });
    }
  };

  return (
    <VStack flexGrow={1} position="relative">
      <SearchInput
        value={query}
        getRef={(ref) => (searchInputRef.current = ref)}
        autoCorrect
        onChangeText={handleSearch}
        onFocus={() => setDropOpen(true)}
        flexGrow={1}
        h="50px"
        mx="10"
        zIndex={9999}
        fontSize="lg"
        shadow="5"
        borderColor="primary.600"
        borderWidth="1"
        placeholderTextColor="primary.600"
        bgColor="white.50"
        rightElement={
          isLoading ? (
            <Spinner colorScheme="primary" mx="2" />
          ) : (
            <Button variant="ghost" rounded="full" onPress={clearText}>
              <Icon
                as={<MaterialCommunityIcons name="close-circle" />}
                size="md"
                color="gray.600"
              />
            </Button>
          )
        }
      />
      <Backdrop open={dropOpen} onPress={() => setDropOpen(false)} />
      {dropOpen && !isLoading && !disableBuiltInSearch && (
        <VStack
          position="absolute"
          top="100%"
          zIndex={99999}
          w="full"
          maxH="500px"
          overflowY="scroll"
          bgColor="white.50"
          p="4"
          m="2"
          shadow="6"
          rounded="md"
          flexGrow={1}>
          {!!products?.length && (
            <>
              <HStack my="2" space="4" alignItems="center">
                <Text fontSize="md" bold flexGrow={1}>
                  {products.length} de {total} resultados
                </Text>
                <Button variant="outline" onPress={onPressViewMore}>
                  Ver mais
                </Button>
              </HStack>
              <List>
                {products?.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    showStoreName={!storeId}
                    onSelectProduct={() => {
                      setDropOpen(false);
                      onSelectProduct(product._id);
                    }}
                  />
                ))}
              </List>
              <Button my="2" variant="outline" onPress={onPressViewMore}>
                Ver mais
              </Button>
            </>
          )}
          {!products?.length && (
            <EmptyCard currentSearch={debouncedQuery} onSelectRecentQuery={handleSearch} />
          )}
        </VStack>
      )}
    </VStack>
  );
};

const List = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
`;

const Backdrop = styled.Pressable<{ open: boolean }>`
  position: fixed;
  width: 100vw;
  height: 100%;
  top: 0;
  left: 0;
  background-color: transparent;
  z-index: 999;
  opacity: 0.1;
  display: ${(props) => (props.open ? "block" : "none")};
  cursor: default;
`;

export default WebSearchDropdown;
